import { useEffect, useRef } from 'react'

// The "visible" zone is tighter than the viewport so we scroll a bit BEFORE
// the active verse reaches the edge — less jarring than waiting until it's
// already half off-screen. Top margin is smaller than bottom because the
// next verse usually appears below the current one during forward playback.
const ROOT_MARGIN = '-15% 0px -25% 0px'

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

export function useFollowActiveVerse(activeIndex: number, ayahCount: number): void {
  const visibleRef = useRef<Set<number>>(new Set())
  const observerRef = useRef<IntersectionObserver | null>(null)
  // Skip scroll on the very first render; only react to subsequent activeIndex changes.
  const hasMountedRef = useRef(false)
  // Tracks the previous active index so we can detect forward vs jump-back
  // motion. Forward advances (most common during audio playback) are scrolled
  // INSTANTLY because smooth-scroll's animation (which can take 1-2s when the
  // target is off-screen) makes the visual focus feel like it lags behind the
  // audio. Manual jumps (e.g. clicking an ayah far away) still use smooth so
  // the user keeps spatial context.
  const prevActiveRef = useRef<number>(-1)

  // (Re)build the observer whenever the count changes.
  useEffect(() => {
    if (typeof window === 'undefined') return
    if (typeof IntersectionObserver === 'undefined') return

    const visible = visibleRef.current
    visible.clear()

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const el = entry.target as HTMLElement
          const idStr = el.id.replace(/^verse-/, '')
          const idx = parseInt(idStr, 10)
          if (Number.isNaN(idx)) continue
          if (entry.isIntersecting) visible.add(idx)
          else visible.delete(idx)
        }
      },
      { rootMargin: ROOT_MARGIN, threshold: 0 }
    )
    observerRef.current = observer

    for (let i = 0; i < ayahCount; i++) {
      const el = document.getElementById(`verse-${i}`)
      if (el) observer.observe(el)
    }

    return () => {
      observer.disconnect()
      observerRef.current = null
    }
  }, [ayahCount])

  // React to active-index changes.
  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true
      prevActiveRef.current = activeIndex
      return
    }
    const prev = prevActiveRef.current
    prevActiveRef.current = activeIndex
    if (activeIndex < 0) return
    const el = document.getElementById(`verse-${activeIndex}`)
    if (!el) return

    // On a forward advance (the dominant audio-playback case) we ALWAYS
    // scroll, even when the IntersectionObserver thinks the new verse is
    // already visible. The IO zone (-15%/-25% margins) lets a verse count
    // as "visible" while it's still pinned to the bottom edge — the user
    // sees the highlight but their gaze is still up at the previous one.
    // `block: 'center'` then puts the active verse roughly in the middle
    // of the viewport, where the eye naturally lands. For non-advance
    // changes (skip-forward, jump-back) we still respect the IO so we
    // don't yank the user's view around when their target is already
    // comfortably in frame.
    const isForwardAdvance = activeIndex === prev + 1
    if (!isForwardAdvance && visibleRef.current.has(activeIndex)) return

    const behavior: ScrollBehavior = prefersReducedMotion() || isForwardAdvance ? 'auto' : 'smooth'

    el.scrollIntoView({
      behavior,
      block: 'center',
    })
  }, [activeIndex])
}
